import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { 
  FileText, 
  AlertCircle, 
  RefreshCw, 
  CheckCircle, 
  ArrowRight, 
  ShieldAlert, 
  FileSearch, 
  TrendingUp,
  History
} from 'lucide-react';
import { Link } from 'react-router-dom';

const SarahDenialManagementPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 relative overflow-hidden">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-blue-50 via-indigo-50/50 to-blue-100/70"></div>
        
        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 backdrop-blur-sm text-blue-700 text-xs font-medium border border-blue-200/50">
                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full"></div>
                <span>Meet Sarah, Denial Management Agent</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Meet Sarah, Your
                <span className="bg-gradient-to-r from-blue-900 via-blue-600 to-blue-400 bg-clip-text text-transparent"> AI Denial Management Agent</span>
              </h1>
              
              <p className="text-lg text-gray-600 leading-relaxed max-w-lg">
                Sarah automatically identifies denied claims, analyzes the reason code, generates the correct appeal documentation, and resubmits the claim instantly.
              </p>
              
              <div className="flex flex-wrap gap-3 pt-2">
                <Link to="/book-demo">
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 rounded-xl text-lg transition-all duration-300 hover:scale-105 shadow-lg shadow-blue-200">
                    Get a Demo
                  </Button>
                </Link>
                <Button variant="outline" className="px-8 py-6 rounded-xl text-lg border-2 hover:bg-gray-50">
                  See Pricing
                </Button>
              </div>
            </div>
            
            {/* Right: Visual */}
            <div className="relative h-[600px] w-full flex items-center justify-center">
                <div className="w-full max-w-lg bg-white rounded-[2rem] shadow-2xl border border-slate-200 p-8 relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-red-500 to-orange-500"></div>
                    
                    <div className="mb-8 flex justify-between items-center">
                        <div>
                            <h3 className="text-2xl font-bold text-slate-800">Denial Queue</h3>
                            <p className="text-slate-500 text-sm">3 claims require attention</p>
                        </div>
                        <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                            <AlertCircle className="w-6 h-6 text-red-500" />
                        </div>
                    </div>

                    <div className="space-y-4">
                        {/* Denial Card 1 */}
                        <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 relative group overflow-hidden">
                            <div className="flex justify-between items-start mb-2">
                                <div>
                                    <div className="font-bold text-slate-700">Claim #8821</div>
                                    <div className="text-xs text-slate-500">Code CO-16</div>
                                </div>
                                <div className="text-red-500 text-xs font-bold bg-red-50 px-2 py-1 rounded">Denied</div>
                            </div>
                            <p className="text-sm text-slate-600 mb-3">Claim/service lacks information...</p>
                            
                            {/* Sarah's Action Overlay */}
                            <div className="absolute inset-0 bg-white/90 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                <div className="flex items-center gap-2 text-green-600 font-bold mb-1">
                                    <RefreshCw className="w-5 h-5" />
                                    Resubmitting
                                </div>
                                <div className="text-xs text-slate-500">Attached Narrative & X-Ray</div>
                            </div>
                        </div>

                        {/* Denial Card 2 */}
                        <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                            <div className="flex justify-between items-start mb-2">
                                <div>
                                    <div className="font-bold text-slate-700">Claim #9932</div>
                                    <div className="text-xs text-slate-500">Code CO-22</div>
                                </div>
                                <div className="text-red-500 text-xs font-bold bg-red-50 px-2 py-1 rounded">Denied</div>
                            </div>
                            <p className="text-sm text-slate-600">Coordination of benefits needed.</p>
                        </div>
                    </div>

                    {/* Bottom Status */}
                    <div className="mt-8 pt-6 border-t border-slate-100 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-blue-600 overflow-hidden border-2 border-white shadow-md">
                            <img src="/assets/sarah.png" alt="Sarah" className="w-full h-full object-cover" /> 
                        </div>
                        <div>
                            <div className="text-sm font-bold text-slate-700">Sarah is working</div>
                            <div className="text-xs text-blue-500">Generating appeal letters...</div>
                        </div>
                    </div>
                </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Cards Section */}
      <section className="py-24 px-6 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Recover Lost Revenue
            </h2>
            <p className="text-lg text-gray-600">
              Most practices write off 3-5% of revenue due to unappealed denials. Sarah recovers that money automatically.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Card 1 */}
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:shadow-lg transition-all group">
              <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6 text-blue-600 group-hover:scale-110 transition-transform">
                <FileSearch className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Root Cause Analysis</h3>
              <p className="text-gray-600">
                Sarah reads the CARC codes on the ERA to understand exactly why the claim was rejected.
              </p>
            </div>

            {/* Card 2 */}
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:shadow-lg transition-all group">
              <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6 text-purple-600 group-hover:scale-110 transition-transform">
                <FileText className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Auto-Appeals</h3>
              <p className="text-gray-600">
                She instantly generates a customized appeal letter citing clinical necessity and attaches supporting evidence.
              </p>
            </div>

            {/* Card 3 */}
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:shadow-lg transition-all group">
              <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6 text-green-600 group-hover:scale-110 transition-transform">
                <History className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Timely Filing</h3>
              <p className="text-gray-600">
                Never miss an appeal deadline. Sarah tracks filing limits for every payer and prioritizes tasks accordingly.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Workflow Visualization */}
      <section className="py-24 px-6 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-red-600/20 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-blue-600/20 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2"></div>

        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-red-400 font-bold tracking-wider text-xs uppercase mb-4">
                <ShieldAlert className="w-4 h-4" />
                The Recovery Process
              </div>
              <h2 className="text-3xl md:text-5xl font-bold mb-6 leading-tight">
                How Sarah Works
              </h2>
              <p className="text-slate-400 text-lg leading-relaxed mb-8">
                She acts as a relentless advocate for your practice, fighting for every dollar you're owed.
              </p>
              
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-red-600 flex items-center justify-center flex-shrink-0 font-bold">1</div>
                  <div>
                    <h4 className="text-xl font-bold mb-1">Detects Denial</h4>
                    <p className="text-slate-400 text-sm">Flags denied claims from incoming ERAs instantly.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-orange-600 flex items-center justify-center flex-shrink-0 font-bold">2</div>
                  <div>
                    <h4 className="text-xl font-bold mb-1">Diagnoses Issue</h4>
                    <p className="text-slate-400 text-sm">Identifies if it's coding, eligibility, or documentation related.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0 font-bold">3</div>
                  <div>
                    <h4 className="text-xl font-bold mb-1">Prepares Appeal</h4>
                    <p className="text-slate-400 text-sm">Writes a clinical narrative and gathers attachments.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0 font-bold">4</div>
                  <div>
                    <h4 className="text-xl font-bold mb-1">Resubmits</h4>
                    <p className="text-slate-400 text-sm">Sends the corrected claim back to the payer.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
               <div className="bg-slate-800/50 backdrop-blur-xl rounded-3xl p-8 border border-slate-700 shadow-2xl">
                  <div className="flex items-center justify-between mb-8">
                      <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-blue-600/20 flex items-center justify-center">
                              <FileText className="w-5 h-5 text-blue-400" />
                          </div>
                          <div>
                              <div className="font-bold text-white">Appeal Generated</div>
                              <div className="text-xs text-slate-400">Target: Aetna</div>
                          </div>
                      </div>
                      <div className="text-xs font-mono text-green-400 font-bold">Ready to Send</div>
                  </div>

                  <div className="bg-white text-slate-900 p-6 rounded-lg font-serif text-sm leading-relaxed shadow-lg relative">
                      <div className="absolute top-2 right-2 text-xs text-slate-400 font-sans">Preview</div>
                      <p className="mb-2 font-bold">Re: Appeal for Claim #9921</p>
                      <p className="mb-2">To Whom It May Concern,</p>
                      <p>
                          This letter is an appeal for the denial of procedure D2740 (Crown) for patient John Doe. 
                          The clinical notes indicate recurrent decay on the mesial margin of the existing restoration...
                      </p>
                      <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-2 text-xs font-sans text-slate-500">
                          <CheckCircle className="w-3 h-3 text-green-500" />
                          <span>X-Rays Attached</span>
                      </div>
                  </div>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* ROI Stats */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-blue-50 p-8 rounded-3xl text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">35%</div>
              <div className="text-gray-600 font-medium">Higher Recovery Rate</div>
            </div>
            <div className="bg-indigo-50 p-8 rounded-3xl text-center">
              <div className="text-4xl font-bold text-indigo-600 mb-2">12 Days</div>
              <div className="text-gray-600 font-medium">Faster Resolution</div>
            </div>
            <div className="bg-purple-50 p-8 rounded-3xl text-center">
              <div className="text-4xl font-bold text-purple-600 mb-2">$42k</div>
              <div className="text-gray-600 font-medium">Annual Recovered Revenue</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Stop leaving money on the table.
          </h2>
          <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
            Let Sarah fight for your claims so you get paid for the work you do.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/book-demo">
              <Button className="bg-blue-600 hover:bg-blue-700 text-white px-10 py-6 rounded-full text-lg shadow-xl shadow-blue-200 hover:shadow-blue-300 hover:-translate-y-1 transition-all">
                Start Recovering Revenue
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SarahDenialManagementPage;